import { WarningCircleIcon } from '@phosphor-icons/react';
import {
  AlertContainer as Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/utils/formatters';

interface ShipAndDebitStatsProps {
  isLoading: boolean;
  error: Error | null;
  data?: {
    transactionCount: number;
    totalDebitAmount: number;
    vendorCount: number;
  } | null;
}

export function ShipAndDebitStats({
  isLoading,
  error,
  data,
}: ShipAndDebitStatsProps) {
  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load ship and debit statistics. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Pending Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              data?.transactionCount || '0'
            )}
          </div>
          {isLoading ? (
            <Skeleton className="h-4 w-32 mt-1" />
          ) : (
            <p className="text-xs text-muted-foreground">
              Across {data?.vendorCount || 0} vendors
            </p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              formatCurrency(data?.totalDebitAmount, {
                locale: 'en-CA',
                currency: 'CAD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground">To be debited</p>
        </CardContent>
      </Card>
    </div>
  );
}
